import { useEffect, useState } from 'react'
import { loadPlayerProfile, loadPlayerGameweekPicks } from '../api/players'
import {
  emptyOtherPlayerGameweekPicks,
  type OtherPlayerGameweekPicks,
} from '../otherPlayerReveal'
import { computeSeasonStats, computeFormHistory } from '../season'
import { computeCalibrationState, type CalibrationState } from '../calibration'
import { findTeamId, teamAbbr } from '../teams'
import { initialsFromName } from '../textUtils'
import type { AvatarPresetId } from '../avatarPresets'
import type { SeasonStats, UserProfile } from '../types'

export interface OpenTopScorer {
  userId: string
  displayName: string
  gameweek: number
}

export interface TopScorerController {
  /** Who the modal is showing, or null when it is closed. */
  open: OpenTopScorer | null
  show: (userId: string, displayName: string, gameweek: number) => void
  close: () => void
  /** Their identity as the modal renders it. Crest and avatar are null until their profile
   *  lands — the modal never guesses a club, the same rule the visited profile follows. */
  profile: UserProfile
  /** Their season rating and how far through calibration they are, both computed from their real
   *  scored history. Zeroed while the fetch is in flight, which reads as "still calibrating" and
   *  therefore renders no tier — the honest state before their history is known. */
  seasonStats: SeasonStats
  calibration: CalibrationState
  /** Their picks for the open gameweek, or the empty bundle while in flight. */
  picks: OtherPlayerGameweekPicks
}

/**
 * The Predict page's top-scorer modal: who it is showing, and their real server-held data.
 *
 * Nothing here is a stand-in. Identity and season history come from `/players/:id`, picks from
 * `/players/:id/picks/:gameweek`, and the Intuition Rating tier is derived from that history by
 * the same `computeSeasonStats` the player's own profile uses — so the tier under their name in
 * this modal is the tier on their profile, by construction.
 *
 * Both fetches are keyed by who and what they were fetched for and only applied once that key
 * matches what is open, so a response for a previously-opened player can never be painted under
 * the current one's name.
 */
export function useTopScorer(): TopScorerController {
  const [open, setOpen] = useState<OpenTopScorer | null>(null)
  const [loaded, setLoaded] = useState<{
    userId: string
    favouriteTeam: string | null
    avatarPreset: AvatarPresetId | null
    seasonStats: SeasonStats
    calibration: CalibrationState
  } | null>(null)
  const [picks, setPicks] = useState<{
    userId: string
    gameweek: number
    picks: OtherPlayerGameweekPicks
  } | null>(null)

  const userId = open?.userId
  const gameweek = open?.gameweek ?? null

  useEffect(() => {
    if (!userId) return
    let cancelled = false
    loadPlayerProfile(userId)
      .then((live) => {
        if (cancelled) return
        const history = live.seasonHistory ?? []
        setLoaded({
          userId,
          favouriteTeam: live.favouriteTeam,
          avatarPreset: live.avatarPreset,
          seasonStats: computeSeasonStats(history),
          // Form history has exactly one point per gameweek they were scored in, which is what
          // calibration counts — never the season's elapsed gameweeks.
          calibration: computeCalibrationState(
            computeFormHistory(history).map((point) => point.gameweek),
            gameweek ?? undefined
          ),
        })
      })
      .catch((error) => {
        // Their identity stays blank: no club, no avatar, no tier. There is no substitute for a
        // person's own club, and none for a rating they may not have earned yet.
        console.error('Failed to load the top scorer’s profile', error)
      })
    return () => {
      cancelled = true
    }
    // `gameweek` only places the calibration window for a player with nothing scored, who has no
    // tier either way — refetching their profile on a gameweek change would be a wasted request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  useEffect(() => {
    if (!userId || gameweek === null) return
    let cancelled = false
    loadPlayerGameweekPicks(userId, gameweek)
      .then((loaded) => {
        if (cancelled) return
        setPicks({ userId, gameweek, picks: loaded })
      })
      .catch(() => {
        // Leave the empty bundle in place — see emptyOtherPlayerGameweekPicks.
      })
    return () => {
      cancelled = true
    }
  }, [userId, gameweek])

  const matched = loaded && loaded.userId === userId ? loaded : null
  const matchedPicks =
    picks && picks.userId === userId && picks.gameweek === gameweek
      ? picks.picks
      : emptyOtherPlayerGameweekPicks()

  const name = open?.displayName.trim() ?? ''
  const favouriteTeam = findTeamId(matched?.favouriteTeam)

  return {
    open,
    show: (userId, displayName, gameweek) => setOpen({ userId, displayName, gameweek }),
    close: () => setOpen(null),
    profile: {
      name: name || 'Unknown player',
      initials: name ? initialsFromName(name) : '??',
      favouriteTeam,
      favouriteTeamAbbr: favouriteTeam ? teamAbbr(favouriteTeam) : '',
      avatarPreset: matched?.avatarPreset ?? null,
    },
    seasonStats: matched?.seasonStats ?? EMPTY_SEASON_STATS,
    calibration: matched?.calibration ?? CALIBRATING,
    picks: matchedPicks,
  }
}

const EMPTY_SEASON_STATS: SeasonStats = {
  correctPredictions: 0,
  totalGamesPredicted: 0,
  accuracyPct: null,
  correctOutcomeCount: 0,
  correctOutcomePct: null,
  bestGameweek: null,
}

/** Nothing fetched yet is indistinguishable from "no rating to show", and both render no tier. */
const CALIBRATING: CalibrationState = {
  progress: 0,
  isCalibrating: true,
  gameweeks: [],
  unlockGameweek: 0,
}
